import React from "react";
import { formatDistanceToNow } from "date-fns";
import {
  useListNotifications,
  useMarkAsRead,
  UserNotification,
  NotificationType,
  NotificationPriority
} from "@/lib/api/user_notification";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Bell, AlertCircle, Info, AlertTriangle, Zap } from "lucide-react";
import { getPriorityLabel, getTypeLabel } from "./utils";

interface NotificationsListProps {
  page?: number;
  pageSize?: number;
  priorities?: number[];
  types?: string[];
  groups?: string[];
  filter_to?: ("UNREAD")[];
}

// Helper function to get priority styling
function getPriorityStyles(priority: number): {
  cardClass: string;
  badgeClass: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
} {
  const label = getPriorityLabel(priority);

  switch (priority) {
    case NotificationPriority.Urgent:
      return {
        cardClass: "bg-red-50 border-red-200 hover:bg-red-100",
        badgeClass: "bg-red-100 text-red-800 border-red-300",
        icon: Zap,
        label
      };
    case NotificationPriority.High:
      return {
        cardClass: "bg-orange-50 border-orange-200 hover:bg-orange-100",
        badgeClass: "bg-orange-100 text-orange-800 border-orange-200",
        icon: AlertTriangle,
        label
      };
    case NotificationPriority.Medium:
      return {
        cardClass: "bg-yellow-50 hover:bg-yellow-100",
        badgeClass: "bg-yellow-100 text-yellow-800 border-yellow-200",
        icon: AlertCircle,
        label
      };
    case NotificationPriority.Low:
      return {
        cardClass: "bg-blue-50  hover:bg-blue-100",
        badgeClass: "bg-blue-100 text-blue-800 border-blue-200",
        icon: Info,
        label
      };
    case NotificationPriority.Background:
    default:
      return {
        cardClass: "bg-gray-50 hover:bg-gray-100",
        badgeClass: "bg-gray-100 text-gray-700 border-gray-200",
        icon: Bell,
        label
      };
  }
}

// Helper function to get type styling
function getTypeStyles(type: string): {
  badgeClass: string;
  label: string;
} {
  const label = getTypeLabel(type);

  switch (type) {
    case NotificationType.System:
      return {
        badgeClass: "bg-gray-100 text-gray-700 border-gray-300",
        label
      };
    case NotificationType.RiskCustomerActivities:
      return {
        badgeClass: "bg-red-100 text-red-700 border-red-300",
        label
      };
    default:
      return {
        badgeClass: "bg-blue-100 text-blue-700 border-blue-300",
        label
      };
  }
}

// Individual notification item component
interface NotificationItemProps {
  notification: UserNotification;
}

function NotificationItem({ notification }: NotificationItemProps) {
  const priorityStyles = getPriorityStyles(notification.notification_priority);
  const typeStyles = getTypeStyles(notification.notification_type);
  const PriorityIcon = priorityStyles.icon;

  const formatTimestamp = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatDistanceToNow(dateObj, { addSuffix: true });
  };

  return (
    <Card className={`transition-colors duration-200 ${priorityStyles.cardClass} ${!notification.is_read ? 'ring-2 ring-blue-200' : ''
      }`}>
      <CardContent className="p-4">
        {/* Header with priority, title, and type */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Read/Unread indicator */}
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${notification.is_read ? 'bg-gray-300' : 'bg-blue-500'
              }`} />

            {/* Priority badge */}
            <Badge variant="outline" className={`${priorityStyles.badgeClass} text-xs flex items-center gap-1 flex-shrink-0`}>
              <PriorityIcon className="w-3 h-3" />
              {priorityStyles.label}
            </Badge>

            {/* Title */}
            {notification.title && (
              <h3 className={`font-medium text-sm truncate ${notification.is_read ? 'text-gray-700' : 'text-gray-900'
                }`}>
                {notification.title}
              </h3>
            )}
          </div>

          {/* Type badge */}
          <Badge variant="outline" className={`${typeStyles.badgeClass} text-xs flex-shrink-0`}>
            {typeStyles.label}
          </Badge>
        </div>

        {/* Message content */}
        <div className={`text-sm mb-3 ${notification.is_read ? 'text-gray-600' : 'text-gray-800'
          }`}>
          {notification.message}...
        </div>

        {/* Footer with group and timestamp */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            {notification.grouping && (
              <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-gray-200">
                {notification.grouping}
              </Badge>
            )}
          </div>

          <time className="flex-shrink-0">
            {formatTimestamp(notification.created_at)}
          </time>
        </div>

        {/* Link indicator */}
        {notification.link && (
          <a className="mt-2 pt-2 border-t border-gray-200" href={notification.link} target="_blank">
            <span className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer">
              View details
            </span>
          </a>
        )}
      </CardContent>
    </Card>
  );
}

// Loading skeleton component
function NotificationSkeleton() {
  return (
    <Card className="bg-gray-50 border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-1">
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="w-16 h-5" />
            <Skeleton className="w-32 h-5" />
          </div>
          <Skeleton className="w-12 h-5" />
        </div>
        <Skeleton className="w-full h-4 mb-2" />
        <Skeleton className="w-3/4 h-4 mb-3" />
        <div className="flex items-center justify-between">
          <Skeleton className="w-16 h-4" />
          <Skeleton className="w-20 h-4" />
        </div>
      </CardContent>
    </Card>
  );
}

// Main notifications list component
export default function NotificationsList({
  page = 1,
  pageSize = 15,
  priorities,
  types,
  groups,
  filter_to
}: NotificationsListProps) {
  const params = {
    page,
    pageSize,
    priorities,
    types,
    groups,
    filter_to
  };

  const { data, isLoading, error, isError } = useListNotifications(params);
  const markAsRead = useMarkAsRead();

  const notifications = data?.data?.data || [];

  // Mark unread notifications as read when they're rendered
  React.useEffect(() => {
    if (!isLoading && notifications.length > 0) {
      const unreadNotificationIds = notifications
        .filter((notification: UserNotification) => !notification.is_read)
        .map((notification: UserNotification) => notification.id);

      if (unreadNotificationIds.length > 0) {
        markAsRead(unreadNotificationIds).catch((error) => {
          console.error('Failed to mark notifications as read:', error);
        });
      }
    }
  }, [notifications, isLoading, markAsRead]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 4 }, (_, index) => (
          <NotificationSkeleton key={index} />
        ))}
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-800">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Failed to load notifications</h3>
              <p className="text-sm text-red-700 mt-1">Please try again later.</p>
              {error instanceof Error && (
                <details className="mt-2 text-xs text-red-600">
                  <summary className="cursor-pointer">Error details</summary>
                  <pre className="mt-1 whitespace-pre-wrap font-mono">{error.message}</pre>
                </details>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <Bell className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
        <p className="text-gray-500">
          {filter_to?.includes("UNREAD") || priorities?.length || types?.length || groups?.length
            ? "No notifications match your current filters."
            : "You don't have any notifications yet."}
        </p>
      </div>
    );
  }

  // Main list render
  return (
    <div className="space-y-4">
      {notifications.map((notification: UserNotification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
        />
      ))}

      {/* Show total count if available */}
      {notifications.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-500">
          Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
